import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, ShoppingCart, X, Truck, CreditCard, Wallet, ExternalLink, UserPlus, GitBranch, PackagePlus, FileSignature, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const INSUMO_LABELS = { caseina: "Caseína", base: "Base", cuajo: "Cuajo", sal: "Sal", cloruro: "Cloruro de calcio" };
const labelOf = (t) => ({ mp: "MP directo", cream: "Crema", curd: "Cuajada", insumo: "Insumo" }[t] || t);

export default function Ventas() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("ventas");
  const [sales, setSales] = useState([]);
  const [inv, setInv] = useState({ mp: [], cream: [], curd: [], insumo: [] });
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsCustomerId, setDocsCustomerId] = useState("_all");
  const [docsSearch, setDocsSearch] = useState("");
  const [docsLoading, setDocsLoading] = useState(false);

  const emptyForm = { customer_id: "", customer_name: "", sale_date: new Date().toISOString().slice(0, 10), payment_method: "contado", credit_days: 0, shipment_folio: "", requires_shipment: false, notes: "", delivery_address: "" };
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([{ source_type: "cream", source_folio: "", quantity: "", unit_price: "" }]);

  const load = async () => {
    const [s, i, c] = await Promise.all([api.get("/sales"), api.get("/inventory"), api.get("/customers")]);
    setSales(s.data);
    setInv(i.data);
    setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const loadDocs = async () => {
    setDocsLoading(true);
    try {
      const params = {};
      if (docsCustomerId !== "_all") params.customer_id = docsCustomerId;
      if (docsSearch.trim()) params.q = docsSearch.trim();
      const { data } = await api.get("/delivery-documents", { params });
      setDocs(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar los documentos generados");
    } finally {
      setDocsLoading(false);
    }
  };
  useEffect(() => {
    if (tab !== "documentos") return;
    const t = setTimeout(loadDocs, 300);
    return () => clearTimeout(t);
  }, [tab, docsCustomerId, docsSearch]);

  const options = useMemo(() => ({
    mp: (inv.mp || []).filter((x) => x.liters_available > 0).map((x) => ({ folio: x.folio, avail: x.liters_available, unit: "L", label: `${x.folio} · ${x.liters_available} L · ${x.supplier}` })),
    cream: (inv.cream || []).filter((x) => x.kg_available > 0).map((x) => ({ folio: x.folio, avail: x.kg_available, unit: "kg", label: `${x.folio} · ${x.kg_available} kg` })),
    curd: (inv.curd || []).filter((x) => x.kg_available > 0).map((x) => ({ folio: x.folio, avail: x.kg_available, unit: "kg", label: `${x.folio} · ${x.kg_available} kg` })),
    insumo: (inv.insumo || []).filter((x) => x.kg_available > 0).map((x) => ({ folio: x.code, avail: x.kg_available, unit: "kg", label: `${INSUMO_LABELS[x.code] || x.code} · ${x.kg_available} kg · costo ${money(x.avg_unit_cost)}/kg` })),
  }), [inv]);

  const updateRow = (i, k, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [k]: v };
    if (k === "source_type") next[i].source_folio = "";
    setRows(next);
  };
  const addRow = () => setRows([...rows, { source_type: "cream", source_folio: "", quantity: "", unit_price: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const total = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.unit_price) || 0), 0), [rows]);

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, credit_days: c.default_credit_days || 0, payment_method: (c.default_credit_days || 0) > 0 ? "credito" : "contado" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name) return toast.error("Selecciona un cliente");
    const valid = rows.filter((r) => r.source_folio && parseFloat(r.quantity) > 0 && parseFloat(r.unit_price) > 0);
    if (!valid.length) return toast.error("Agrega al menos un item con cantidad y precio");
    try {
      const items = valid.map((r) => {
        const unit = r.source_type === "mp" ? "L" : "kg";
        return { source_type: r.source_type, source_folio: r.source_folio, quantity: parseFloat(r.quantity), unit, unit_price: parseFloat(r.unit_price) };
      });
      const payload = {
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        sale_date: form.sale_date,
        payment_method: form.payment_method,
        credit_days: form.payment_method === "credito" ? parseInt(form.credit_days) || 0 : 0,
        shipment_folio: form.shipment_folio,
        requires_shipment: !!form.requires_shipment,
        notes: form.notes,
        items,
        delivery_address: form.delivery_address,
      };
      const { data } = await api.post("/sales", payload);
      const suffix = data.balance > 0 ? ` · saldo ${money(data.balance)}` : "";
      toast.success(`Venta ${data.folio} registrada · ${money(data.total)}${suffix}`);
      setForm(emptyForm);
      setRows([{ source_type: "cream", source_folio: "", quantity: "", unit_price: "" }]);
      setOpen(false);
      load();
      if (payload.requires_shipment && !payload.shipment_folio) {
        // Redirigir a Embarques para crear el embarque de esta venta
        setTimeout(() => navigate(`/embarques?sale=${encodeURIComponent(data.folio)}&customer=${encodeURIComponent(data.customer_name)}${data.customer_id ? `&customer_id=${data.customer_id}` : ""}`), 400);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const kpis = useMemo(() => {
    const now = sales;
    const totalMonth = now.reduce((s, x) => s + (x.total || 0), 0);
    const pendingBalance = now.reduce((s, x) => s + (x.balance || 0), 0);
    return { count: now.length, totalMonth, pendingBalance };
  }, [sales]);

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="ventas-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Ventas</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Registra ventas de MP, crema, cuajada e insumos. El inventario se descuenta automáticamente.</p>
        </div>
        {!open && (
          <Button onClick={() => setOpen(true)} data-testid="new-sale-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva venta
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI icon={ShoppingCart} label="Ventas registradas" value={kpis.count} unit="" />
        <KPI icon={Wallet} label="Total facturado" value={money(kpis.totalMonth)} unit="" />
        <KPI icon={CreditCard} label="Saldo pendiente cobrar" value={money(kpis.pendingBalance)} unit="" accent="amber" />
      </div>

      {open && (
        <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">Nueva venta</h2>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cliente *</Label>
                <div className="flex gap-1.5 mt-1.5">
                  <Select value={form.customer_id || ""} onValueChange={pickCustomer}>
                    <SelectTrigger data-testid="sale-customer-select"><SelectValue placeholder={customers.length ? "Elegir cliente registrado…" : "Sin clientes registrados"} /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.default_credit_days > 0 ? ` · ${c.default_credit_days}d crédito` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                  <Link to="/clientes"><Button type="button" variant="outline" size="icon" title="Nuevo cliente"><UserPlus className="w-4 h-4" /></Button></Link>
                </div>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })} className="mt-1.5" data-testid="sale-customer-name" placeholder="… o captura nombre libre" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Fecha</Label>
                <Input type="date" value={form.sale_date} onChange={(e) => setForm({ ...form, sale_date: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Folio embarque</Label>
                <Input value={form.shipment_folio} onChange={(e) => setForm({ ...form, shipment_folio: e.target.value })} className="mt-1.5" placeholder="EMB-... (opcional)" data-testid="sale-shipment-folio" />
                <label className="mt-2 flex items-center gap-2 cursor-pointer select-none" data-testid="sale-requires-shipment-wrap">
                  <input
                    type="checkbox"
                    checked={!!form.requires_shipment}
                    onChange={(e) => setForm({ ...form, requires_shipment: e.target.checked })}
                    data-testid="sale-requires-shipment"
                    className="w-4 h-4 rounded border-stone-300 text-[#1E3A8A] focus:ring-[#1E3A8A]"
                  />
                  <span className="text-xs text-stone-600 dark:text-stone-400 flex items-center gap-1">
                    <Truck className="w-3 h-3" /> ¿Requiere embarque? (te llevaré a crearlo al guardar)
                  </span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Forma de pago</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado"><span className="flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Contado</span></SelectItem>
                    <SelectItem value="credito"><span className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Crédito</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Días de crédito</Label>
                <Input type="number" min="0" disabled={form.payment_method === "contado"} value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: e.target.value })} className="mt-1.5" placeholder={form.payment_method === "contado" ? "N/A" : "Ej. 15, 30..."} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Dirección de entrega de esta venta (opcional, si es distinta a la del cliente)</Label>
                <Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="mt-1.5" placeholder="Déjalo en blanco para usar el domicilio registrado del cliente" data-testid="sale-delivery-address" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400 flex items-center gap-1.5">
                  <ShoppingCart className="w-3.5 h-3.5" /> Items de la venta
                </Label>
                <button type="button" onClick={addRow} className="text-xs text-[#1E3A8A] dark:text-blue-400 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar item</button>
              </div>
              <div className="border border-stone-200 dark:border-stone-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                    <tr>{["Tipo", "Lote / Producto", "Cantidad", "Precio unit.", "Subtotal", ""].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const opts = options[r.source_type] || [];
                      const subtotal = (parseFloat(r.quantity) || 0) * (parseFloat(r.unit_price) || 0);
                      const unit = r.source_type === "mp" ? "L" : "kg";
                      return (
                        <tr key={i} className="border-b border-stone-100 dark:border-stone-800 last:border-0">
                          <td className="px-3 py-2 w-36">
                            <Select value={r.source_type} onValueChange={(v) => updateRow(i, "source_type", v)}>
                              <SelectTrigger className="h-9" data-testid={`sale-type-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mp">MP directo</SelectItem>
                                <SelectItem value="cream">Crema</SelectItem>
                                <SelectItem value="curd">Cuajada</SelectItem>
                                <SelectItem value="insumo">Insumo</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Select value={r.source_folio} onValueChange={(v) => updateRow(i, "source_folio", v)}>
                              <SelectTrigger className="h-9" data-testid={`sale-lot-${i}`}><SelectValue placeholder="Elegir lote..." /></SelectTrigger>
                              <SelectContent>{opts.length === 0 ? <SelectItem value="_none" disabled>Sin disponible</SelectItem> : opts.map((o) => <SelectItem key={o.folio} value={o.folio}>{o.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2 w-32"><Input type="number" step="0.01" value={r.quantity} onChange={(e) => updateRow(i, "quantity", e.target.value)} className="h-9" placeholder={unit} data-testid={`sale-qty-${i}`} /></td>
                          <td className="px-3 py-2 w-32"><Input type="number" step="0.01" value={r.unit_price} onChange={(e) => updateRow(i, "unit_price", e.target.value)} className="h-9" placeholder={`$/${unit}`} data-testid={`sale-price-${i}`} /></td>
                          <td className="px-3 py-2 text-sm tabular-nums font-medium text-stone-900 dark:text-stone-100">{subtotal ? money(subtotal) : "—"}</td>
                          <td className="px-3 py-2">{rows.length > 1 && <button type="button" onClick={() => removeRow(i)} className="text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-stone-50 dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800">
                    <tr><td colSpan={4} className="px-3 py-3 text-right text-sm font-outfit font-semibold text-stone-700 dark:text-stone-300">TOTAL</td><td className="px-3 py-3 text-base font-outfit font-bold tabular-nums text-[#1E3A8A] dark:text-blue-400" data-testid="sale-total">{money(total)}</td><td></td></tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="sale-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Registrar venta</Button>
            </div>
          </form>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 dark:bg-stone-900 p-1 rounded-lg">
          <TabsTrigger value="ventas" data-testid="tab-ventas" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Historial de ventas</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Entregas y pagarés generados</TabsTrigger>
        </TabsList>

        <TabsContent value="ventas">
      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Historial de ventas</h3>
          <span className="text-xs text-stone-500 ml-auto">{sales.length} registradas</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>
                {["Folio", "Fecha", "Cliente", "Items", "Pago", "Vencimiento", "Embarque", "Total", "Saldo", ""].map((h) => <th key={h} className="px-3 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-stone-500">Sin ventas registradas.</td></tr>
              ) : sales.map((s) => {
                const overdue = s.balance > 0 && s.due_date && new Date(s.due_date) < new Date();
                return (
                  <tr key={s.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                    <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400">{s.folio}</td>
                    <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300 tabular-nums">{s.sale_date}</td>
                    <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300">{s.customer_name}</td>
                    <td className="px-3 py-3 text-xs text-stone-600 dark:text-stone-400 max-w-[280px]">
                      {(s.items || []).map((it, i) => (
                        <div key={i}>
                          <span className="text-stone-500">{labelOf(it.source_type)}·</span>
                          <span className="font-mono">{it.source_folio}</span>
                          <span className="text-stone-500"> ({it.quantity}{it.unit})</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {s.payment_method === "credito"
                        ? <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-medium">Crédito {s.credit_days}d</span>
                        : <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-medium">Contado</span>}
                    </td>
                    <td className="px-3 py-3 text-sm tabular-nums">
                      {s.due_date ? <span className={overdue ? "text-red-600 dark:text-red-400 font-semibold" : "text-stone-700 dark:text-stone-300"}>{s.due_date}{overdue && " ⚠"}</span> : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {s.shipment_folio ? (
                        <Link to={`/trazabilidad?lote=${s.shipment_folio}`} className="font-mono text-[#1E3A8A] dark:text-blue-400 hover:underline" data-testid={`sale-shipment-link-${s.folio}`}>{s.shipment_folio}</Link>
                      ) : s.requires_shipment ? (
                        <button
                          onClick={() => navigate(`/embarques?sale=${encodeURIComponent(s.folio)}&customer=${encodeURIComponent(s.customer_name)}${s.customer_id ? `&customer_id=${s.customer_id}` : ""}`)}
                          data-testid={`sale-create-shipment-${s.folio}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60 text-[10px] uppercase tracking-wider font-outfit font-semibold"
                        >
                          <PackagePlus className="w-3 h-3" /> Crear embarque
                        </button>
                      ) : (
                        <span className="text-stone-400">Sin embarque</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm tabular-nums font-medium text-stone-900 dark:text-stone-100">{money(s.total)}</td>
                    <td className="px-3 py-3 text-sm tabular-nums">
                      {(s.balance || 0) > 0
                        ? <span className="text-amber-700 dark:text-amber-400 font-semibold">{money(s.balance)}</span>
                        : <span className="text-emerald-700 dark:text-emerald-400">Pagada</span>}
                    </td>
                    <td className="px-3 py-3"><div className="flex items-center gap-1">
                      <Link to={`/trazabilidad?lote=${s.folio}`} title="Ver en Trazabilidad 360°" data-testid={`trace-btn-${s.folio}`} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><GitBranch className="w-4 h-4" /></Link>
                      <a href={`/ventas/entrega/${s.id}`} target="_blank" rel="noopener noreferrer" title="Generar entrega de producto y pagaré" data-testid={`delivery-doc-btn-${s.folio}`} className="p-1.5 rounded-md text-stone-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"><FileSignature className="w-4 h-4" /></a>
                      <button onClick={() => setToDelete(s)} data-testid={`delete-sale-btn-${s.folio}`} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="documentos">
          <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden" data-testid="delivery-docs-card">
            <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-3 flex-wrap">
              <FileText className="w-4 h-4 text-stone-500" />
              <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Entregas y pagarés generados</h3>
              <span className="text-xs text-stone-500">Consulta cualquier documento ya generado sin buscar venta por venta</span>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <Select value={docsCustomerId} onValueChange={setDocsCustomerId}>
                  <SelectTrigger className="w-56" data-testid="docs-customer-filter"><SelectValue placeholder="Filtrar por cliente…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos los clientes</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input value={docsSearch} onChange={(e) => setDocsSearch(e.target.value)} placeholder="Buscar por nombre…" className="pl-8 w-48" data-testid="docs-search-input" />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                  <tr>{["Cliente", "Folio venta", "Condición", "No. Pagaré", "Saldo actual", "Vence", "Generado", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {docsLoading ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-stone-500" data-testid="docs-loading">Cargando documentos…</td></tr>
                  ) : docs.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-stone-500" data-testid="docs-empty-state">
                      {docsCustomerId !== "_all" || docsSearch.trim()
                        ? "Ningún documento coincide con el filtro."
                        : <>Aún no se ha generado ningún documento. Usa el ícono <FileSignature className="w-3.5 h-3.5 inline" /> en el Historial de ventas.</>}
                    </td></tr>
                  ) : docs.map((d) => (
                    <tr key={d.sale_id} data-testid={`doc-row-${d.sale_folio}`} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                      <td className="px-3 py-3 text-sm font-medium text-stone-800 dark:text-stone-200">{d.customer_name}</td>
                      <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400">{d.sale_folio}</td>
                      <td className="px-3 py-3 text-xs">
                        {d.payment_method === "credito"
                          ? <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-medium">Crédito</span>
                          : <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-medium">Contado</span>}
                      </td>
                      <td className="px-3 py-3 text-xs font-mono text-stone-600 dark:text-stone-400">{d.pagare_number || "—"}</td>
                      <td className="px-3 py-3 text-sm tabular-nums">
                        {d.payment_method === "credito"
                          ? ((d.balance || 0) > 0 ? <span className="text-amber-700 dark:text-amber-400 font-semibold">{money(d.balance)}</span> : <span className="text-emerald-700 dark:text-emerald-400">Pagado</span>)
                          : <span className="text-stone-400">—</span>}
                      </td>
                      <td className="px-3 py-3 text-sm tabular-nums text-stone-600 dark:text-stone-400">{d.due_date || "—"}</td>
                      <td className="px-3 py-3 text-xs text-stone-500 tabular-nums">{d.created_at ? new Date(d.created_at).toLocaleDateString("es-MX") : "—"}{d.regenerated_at && <span className="block text-[10px]">regenerado {new Date(d.regenerated_at).toLocaleDateString("es-MX")}</span>}</td>
                      <td className="px-3 py-3">
                        <a href={`/ventas/entrega/${d.sale_id}`} target="_blank" rel="noopener noreferrer" title="Ver / reimprimir" data-testid={`view-doc-btn-${d.sale_folio}`} className="p-1.5 rounded-md text-stone-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 inline-block"><FileSignature className="w-4 h-4" /></a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar venta {toDelete?.folio}?</AlertDialogTitle><AlertDialogDescription>El inventario correspondiente volverá a estar disponible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={async () => { try { await api.delete(`/sales/${toDelete.id}`); toast.success("Venta eliminada"); setToDelete(null); load(); } catch (err) { toast.error(err.response?.data?.detail || "Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, unit, accent }) => (
  <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-2 font-outfit font-semibold text-2xl text-stone-900 dark:text-stone-100 tabular-nums">
          {value} {unit && <span className="text-base font-medium text-stone-500 ml-1">{unit}</span>}
        </div>
      </div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent === "amber" ? "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400" : "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400"}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);
